import { useEffect, useMemo, useState } from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { NumberRangeFilter } from "@/components/NumberRangeFilter";
import {
  LotteryOption,
  PRODUCT_NUMERIC_COLUMNS,
  ProductFilterLimits,
  ProductNumericColumn,
  ProductsFilters,
  describeRange,
  isRangeActive,
  setRangeFilter,
} from "@/App/Products/filters";

/** Colunas numéricas exibidas na listagem, na mesma ordem das colunas da tabela. */
const NUMERIC_FILTERS: ProductNumericColumn[] = [
  "game_quantity",
  "dez_quantity",
  "quota_quantity",
  "total_with_tariff",
  "sale_price",
  "price_without_tariff",
];

/** Busca textual por nome, com debounce para não requisitar a cada tecla. */
const DebouncedSearchInput = ({
  value,
  onChange,
  placeholder = "Buscar...",
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <Input
      id={id}
      className="h-10"
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onChange(draft);
        }
      }}
    />
  );
};

export type ProductsFiltersCardProps = {
  filters: ProductsFilters;
  onFiltersChange: (filters: ProductsFilters) => void;
  onClear: () => void;
  lotteries?: LotteryOption[];
  /** Limites reais de cada coluna numérica (chave `limits` da API). */
  limits?: ProductFilterLimits;
  /** true enquanto os limites ainda não chegaram da API. */
  loadingLimits?: boolean;
};

/**
 * Card de filtros da listagem de produtos.
 *
 * Todos os filtros ficam visíveis, sem popover: multiselect de loterias,
 * busca por nome e um NumberRangeFilter (inputs + range slider) por coluna
 * numérica. O estado e os parâmetros enviados à API continuam os mesmos.
 */
export const ProductsFiltersCard = ({
  filters,
  onFiltersChange,
  onClear,
  lotteries = [],
  limits = {},
  loadingLimits = false,
}: ProductsFiltersCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);
  const toggleLabel = isExpanded ? "Ocultar filtros" : "Mostrar filtros";

  const selectedLotteries = useMemo(
    () => filters.lottery_id ?? [],
    [filters.lottery_id]
  );

  /** Chips dos filtros ativos - permitem remover um filtro individualmente. */
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];

    selectedLotteries.forEach((lotteryId) => {
      const name =
        lotteries.find((lottery) => String(lottery.id) === String(lotteryId))
          ?.name ?? lotteryId;

      chips.push({
        key: `lottery-${lotteryId}`,
        label: `Loteria: ${name}`,
        onRemove: () =>
          onFiltersChange({
            ...filters,
            lottery_id: selectedLotteries.filter((id) => id !== lotteryId),
          }),
      });
    });

    if (filters.name) {
      chips.push({
        key: "name",
        label: `Nome: ${filters.name}`,
        onRemove: () => onFiltersChange({ ...filters, name: undefined }),
      });
    }

    NUMERIC_FILTERS.forEach((column) => {
      const range = filters.ranges?.[column];
      if (!isRangeActive(range)) return;

      const config = PRODUCT_NUMERIC_COLUMNS[column];
      chips.push({
        key: column,
        label: `${config.label}: ${describeRange(range, config)}`,
        onRemove: () => onFiltersChange(setRangeFilter(filters, column, {})),
      });
    });

    return chips;
  }, [filters, selectedLotteries, lotteries, onFiltersChange]);

  const hasChips = activeChips.length > 0;

  return (
    <Card>
      <CardHeader className="space-y-2 p-4 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-controls="products-filters-content"
            className="group flex select-none items-center gap-2 text-left"
          >
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
            <CardTitle className="text-base font-semibold transition-colors group-hover:text-primary">
              Filtros
            </CardTitle>
            {hasChips && (
              <Badge variant="secondary">
                {activeChips.length}{" "}
                {activeChips.length === 1 ? "ativo" : "ativos"}
              </Badge>
            )}
          </button>
          <div className="flex gap-2">
            {hasChips && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClear}
                className="h-8 gap-1"
              >
                <X className="h-4 w-4" />
                Limpar filtros
              </Button>
            )}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                    aria-controls="products-filters-content"
                    aria-label={toggleLabel}
                    className="h-8 w-8 shrink-0"
                  >
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        isExpanded && "rotate-180"
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{toggleLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Os filtros ativos continuam visíveis mesmo com o card minimizado. */}
        {hasChips && (
          <div className="flex flex-wrap gap-1.5">
            {activeChips.map((chip) => (
              <Badge
                key={chip.key}
                variant="secondary"
                className="flex items-center gap-1 font-normal"
              >
                {chip.label}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-red-500"
                  onClick={chip.onRemove}
                />
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <CardContent id="products-filters-content" className="space-y-4 p-4 pt-0">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Loteria
              </Label>
              <MultiSelectCheckbox
                options={lotteries.map((lottery) => ({
                  value: String(lottery.id),
                  label: lottery.name,
                }))}
                selectedValues={selectedLotteries}
                onSelectionChange={(values) =>
                  onFiltersChange({ ...filters, lottery_id: values })
                }
                placeholder="Todas as loterias"
                searchable
                searchPlaceholder="Buscar loteria..."
                hideBadge
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="products-filter-name"
                className="text-xs font-medium text-muted-foreground"
              >
                Nome
              </Label>
              <DebouncedSearchInput
                id="products-filter-name"
                value={filters.name ?? ""}
                placeholder="Buscar por nome"
                onChange={(name) =>
                  onFiltersChange({ ...filters, name: name || undefined })
                }
              />
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3 p-1">
            {NUMERIC_FILTERS.map((column) => {
              const config = PRODUCT_NUMERIC_COLUMNS[column];
              const range = filters.ranges?.[column];

              return (
                <div key={column} className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">
                    {config.label}
                  </Label>
                  <NumberRangeFilter
                    id={`products-filter-${column}`}
                    value={range}
                    bounds={limits?.[column] ?? null}
                    decimals={config.decimals}
                    format={config.format}
                    loading={loadingLimits}
                    startLabel="De"
                    endLabel="Até"
                    onChange={(nextRange) =>
                      onFiltersChange(setRangeFilter(filters, column, nextRange))
                    }
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};
